import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { Home, Megaphone, Users, UserRound, PencilRuler, Calculator, FileText, FileSignature, Building2, HardHat, ShoppingCart,
  Wallet, ShieldCheck, KeyRound, Wrench, FolderOpen, BarChart3, Sparkles, Settings, Search, Bell, Plus, ChevronsLeft, Menu, CheckSquare, ListChecks, Package, FileSpreadsheet, Shield, Route } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { api, fmtDT } from "../../lib/api";
import { useApp } from "../../lib/ctx";
import { F } from "../../lib/fields";
import RecordForm from "../common/RecordForm";

export const NAV = [
  ["overview", "Overview", "/", Home], ["journey", "Client Journey", "/journey", Route, "crm"], ["marketing", "Marketing", "/marketing", Megaphone], ["crm", "CRM & Sales", "/crm", Users],
  ["customers", "Customers", "/customers", UserRound], ["design", "Design & Planning", "/design", PencilRuler],
  ["estimation", "Estimation & BOQ", "/estimation", Calculator], ["quotations", "Quotations & Proposals", "/quotations", FileText],
  ["packages", "Packages", "/packages", Package, "quotations"], ["package_quotes", "Package Quotations", "/package-quotes", FileSpreadsheet, "quotations"],
  ["agreements", "Agreements & e-Sign", "/agreements", FileSignature], ["projects", "Projects", "/projects", Building2],
  ["site", "Site Operations", "/site", HardHat], ["procurement", "Procurement & Materials", "/procurement", ShoppingCart],
  ["finance", "Finance & Payments", "/finance", Wallet], ["pmc", "PMC & Quality", "/pmc", ShieldCheck], ["handover", "Handover", "/handover", KeyRound],
  ["maintenance", "Maintenance", "/maintenance", Wrench], ["documents", "Documents", "/documents", FolderOpen],
  ["reports", "Reports & Analytics", "/reports", BarChart3], ["ai", "AI Assistant", "/ai", Sparkles], ["admin", "Admin Panel", "/admin", Shield, "settings"], ["settings", "Team & Settings", "/settings", Settings],
];
const ROUTE = { journeys: (id) => `/journey/${id}`, packages: () => "/packages", package_quotes: (id) => `/package-quotes/${id}`, leads: (id) => `/crm?lead=${id}`, customers: (id) => `/customers/${id}`, projects: (id) => `/projects/${id}`, quotations: (id) => `/quotations/${id}`,
  agreements: (id) => `/agreements/${id}`, purchase_orders: () => "/procurement?tab=po", tickets: () => "/maintenance", documents: () => "/documents",
  invoices: () => "/finance", suppliers: () => "/procurement?tab=suppliers" };

function Sidebar({ collapsed, setCollapsed, mobile, setMobile }) {
  const { me } = useApp();
  const items = NAV.filter(([k, , , , m]) => me?.modules?.includes(m || k));
  return (
    <>
      {mobile && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobile(false)} />}
      <aside data-testid="sidebar" className={`fixed lg:sticky top-0 z-40 h-screen bg-[#0D131B] text-white flex flex-col transition-all duration-300
        ${collapsed ? "w-[76px]" : "w-[260px]"} ${mobile ? "left-0" : "-left-[280px] lg:left-0"}`}>
        <div className="h-16 flex items-center px-4 border-b border-white/5">
          <Link to="/" className="flex items-center">
            <img src={collapsed ? "/brand/logo-icon.png" : "/brand/logo-full-dark.png"} alt="ConstructONS" className={collapsed ? "h-9" : "h-9 w-auto"} />
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {items.map(([k, label, to, Icon]) => (
            <NavLink key={k} to={to} end={to === "/"} onClick={() => setMobile(false)} data-testid={`nav-${k}`} title={label}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-colors ${isActive ? "text-white font-medium" : "text-neutral-300 hover:bg-white/5 hover:text-white"}`}
              style={({ isActive }) => (isActive ? { background: "var(--grad-primary)" } : {})}>
              <Icon className="h-[18px] w-[18px] shrink-0" />{!collapsed && <span className="truncate">{label}</span>}
            </NavLink>
          ))}
        </nav>
        <button onClick={() => setCollapsed(!collapsed)} className="hidden lg:flex items-center gap-2 px-5 py-3 text-xs text-neutral-400 hover:text-white border-t border-white/5" data-testid="sidebar-collapse-btn">
          <ChevronsLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />{!collapsed && "Collapse"}
        </button>
      </aside>
    </>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const timer = useRef();
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.length < 2) { setRes([]); return; }
    timer.current = setTimeout(() => { api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => { setRes(r.data); setOpen(true); }).catch(() => {}); }, 250);
  }, [q]);
  return (
    <div className="relative w-full max-w-md">
      <Search className="h-4 w-4 absolute left-3 top-3 text-neutral-400" />
      <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search leads, customers, projects, documents..." className="h-10 w-full rounded-xl bg-neutral-100 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5A00]/30 focus:bg-white" />
      {open && (
        <div className="absolute top-12 left-0 right-0 card-c p-2 z-50 max-h-96 overflow-y-auto" data-testid="global-search-results">
          {res.length === 0 && <p className="text-sm text-neutral-500 p-3">No matching records</p>}
          {res.map((r) => (
            <button key={r.entity + r.id} onMouseDown={() => { nav(ROUTE[r.entity](r.id)); setQ(""); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-orange-50 flex justify-between gap-3" data-testid={`search-result-${r.id}`}>
              <span className="text-sm truncate">{r.label}</span><span className="text-[11px] text-neutral-400 shrink-0">{r.entity.replace("_", " ")} {r.number}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 60000); return () => clearInterval(i); }, []);
  const unread = items.filter((i) => !i.read).length;
  return (
    <Popover onOpenChange={(o) => { if (!o && unread) api.post("/notifications/read").then(load); }}>
      <PopoverTrigger asChild>
        <button className="relative h-10 w-10 rounded-xl hover:bg-neutral-100 flex items-center justify-center" data-testid="notifications-btn">
          <Bell className="h-5 w-5 text-neutral-700" />
          {unread > 0 && <span className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 rounded-full bg-[#FF2D00] text-white text-[10px] flex items-center justify-center" data-testid="notifications-count">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-2xl" data-testid="notifications-panel">
        <p className="h3 px-4 py-3 border-b">Notifications</p>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <p className="text-sm text-neutral-500 p-4">You're all caught up.</p>}
          {items.slice(0, 40).map((n) => (
            <div key={n.id} className={`px-4 py-3 border-b border-neutral-100 ${n.read ? "" : "bg-orange-50/50"}`}>
              <p className="text-sm font-medium">{n.title}</p><p className="text-xs text-neutral-600">{n.body}</p>
              <p className="text-[11px] text-neutral-400 mt-1">{fmtDT(n.at)}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const QUICK = [["leads", "Lead", "crm"], ["customers", "Customer", "customers"], ["tasks", "Task", "overview"], ["site_reports", "Daily site report", "site"],
  ["tickets", "Service ticket", "maintenance"], ["expenses", "Expense", "finance"]];

export default function AppShell({ children }) {
  const { me } = useApp();
  const [collapsed, setCollapsed] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [quick, setQuick] = useState(null);
  const loc = useLocation();
  const crumb = NAV.find(([, , to]) => to !== "/" && loc.pathname.startsWith(to))?.[1] || (loc.pathname === "/" ? "Overview" : loc.pathname.slice(1).replace(/^\w/, (c) => c.toUpperCase()));
  return (
    <div className="flex min-h-screen">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobile={mobile} setMobile={setMobile} />
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 h-16 bg-white/85 backdrop-blur-xl border-b border-neutral-200 flex items-center gap-3 px-4 lg:px-8">
          <button className="lg:hidden" onClick={() => setMobile(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <div className="hidden md:block text-xs text-neutral-500 min-w-[120px]" data-testid="breadcrumb">ConstructONS / <span className="text-[#0D131B] font-medium">{crumb}</span></div>
          <div className="flex-1 flex justify-center"><GlobalSearch /></div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><button className="btn-grad !h-10 !w-10 !px-0 !rounded-full" data-testid="quick-create-btn" title="Quick create"><Plus className="h-5 w-5" /></button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              {QUICK.filter(([, , m]) => m === "overview" || me?.modules?.includes(m)).map(([e, l]) => (
                <DropdownMenuItem key={e} onClick={() => setQuick(e)} data-testid={`quick-create-${e}`}>New {l}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/approvals" className="h-10 w-10 rounded-xl hover:bg-neutral-100 flex items-center justify-center" title="Approvals" data-testid="approvals-link"><CheckSquare className="h-5 w-5 text-neutral-700" /></Link>
          <Link to="/tasks" className="h-10 w-10 rounded-xl hover:bg-neutral-100 hidden sm:flex items-center justify-center" title="My tasks" data-testid="tasks-link"><ListChecks className="h-5 w-5 text-neutral-700" /></Link>
          <Notifications />
          <div data-testid="user-menu"><UserButton /></div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto">{children}</main>
      </div>
      {quick && <RecordForm open entity={quick} fields={F[quick]} onClose={() => setQuick(null)} title={`New ${QUICK.find((x) => x[0] === quick)[1]}`} />}
    </div>
  );
}
